import { TranslocoPipe, provideTranslocoScope, translate } from '@jsverse/transloco';
import { DatePipe, Location } from '@angular/common';
import { TranslocoCurrencyPipe } from '@jsverse/transloco-locale';
import { Component, inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { CanDirective, CapabilityNames } from 'core';
import { LoadingSpinner } from 'design-system';
import { finalize } from 'rxjs';
import { Receipt, ReceiptListSearch, ReceiptStatus } from '../../interfaces/receipt.model';
import { ReceiptsService } from '../../services/receipts.service';
import { WorksheetsService } from '../../services/worksheets.service';
import {
  ReceiptsWorksheetZoneTotalsDialogComponent,
  WorksheetZoneTotalsDialogData,
} from '../../components/dialog-components/receipts-worksheet-zone-totals-dialog-component/receipts-worksheet-zone-totals-dialog-component';

interface ReceiptRow {
  id: string;
  idIncasso: string;
  zona: string;
  cliente: string;
  dataIncasso: string;
  importoContanti: number;
  importoAssegni: number;
  importoTotale: number;
  selezionato: boolean;
}

@Component({
  providers: [provideTranslocoScope('receipts')],
  selector: 'rev-receipts-brogliacci-new',
  imports: [TranslocoPipe,
    CanDirective,
    TranslocoCurrencyPipe,
    DatePipe,
    LoadingSpinner,
    MatButtonModule,
    MatCheckboxModule,
    MatTableModule,
  ],
  templateUrl: './receipts-brogliacci-new.html',
  styleUrl: './receipts-brogliacci-new.scss',
})
export class ReceiptsBrogliacciNew implements OnInit {
  private readonly receiptsService = inject(ReceiptsService);
  private readonly worksheetsService = inject(WorksheetsService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);

  constructor(private location: Location) {}

  readonly CAPS = CapabilityNames;

  incassiDaRendicontare: ReceiptRow[] = [];
  isLoading = false;
  isSaving = false;

  readonly colonneTabella = [
    'id',
    'zona',
    'cliente',
    'dataIncasso',
    'importoContanti',
    'importoAssegni',
    'importoTotale',
    'selezionato',
  ];

  ngOnInit(): void {
    this.loadIncassi();
  }

  loadIncassi(): void {
    this.isLoading = true;

    const filter: ReceiptListSearch = {
      searchText: null,
      orderBy: null,
      searchObject: { statiIncasso: [ReceiptStatus.Chiuso] },
    };

    this.receiptsService
      .getReceiptsList(1, 0, filter)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: data => {
          this.incassiDaRendicontare = (data.items ?? []).map(receipt => this.toRow(receipt));
        },
        error: err => {
          console.error('Errore caricamento incassi da rendicontare', err);
          this.incassiDaRendicontare = [];
        },
      });
  }

  get tuttiInclusi(): boolean {
    return this.incassiDaRendicontare.length > 0
      && this.incassiDaRendicontare.every(incasso => incasso.selezionato);
  }

  get selezioneParziale(): boolean {
    return !this.tuttiInclusi
      && this.incassiDaRendicontare.some(incasso => incasso.selezionato);
  }

  get almenoUnIncassoIncluso(): boolean {
    return this.incassiDaRendicontare.some(incasso => incasso.selezionato);
  }

  get totaleContantiSelezionati(): number {
    return this.calcolaTotaleSelezionato('importoContanti');
  }

  get totaleAssegniSelezionati(): number {
    return this.calcolaTotaleSelezionato('importoAssegni');
  }

  get totaleIncassiSelezionati(): number {
    return this.calcolaTotaleSelezionato('importoTotale');
  }

  impostaTuttiInclusi(inclusi: boolean): void {
    this.incassiDaRendicontare.forEach(incasso => {
      incasso.selezionato = inclusi;
    });
  }

  impostaIncassoIncluso(id: string, incluso: boolean): void {
    const incasso = this.incassiDaRendicontare.find(item => item.id === id);

    if (incasso) {
      incasso.selezionato = incluso;
    }
  }

  mostraTotaliPerZona(): void {
    if (!this.almenoUnIncassoIncluso) {
      return;
    }

    const totalsByZone = new Map<string, number>();

    this.incassiDaRendicontare
      .filter(incasso => incasso.selezionato)
      .forEach(incasso => {
        const zona = incasso.zona.trim() || translate<string>('receipts.ui.zonaNonSpecificata');
        totalsByZone.set(zona, (totalsByZone.get(zona) ?? 0) + incasso.importoTotale);
      });

    const data: WorksheetZoneTotalsDialogData = {
      totaliPerZona: Array.from(totalsByZone, ([zona, totale]) => ({ zona, totale })),
      totaleComplessivo: this.totaleIncassiSelezionati,
    };

    this.dialog.open(ReceiptsWorksheetZoneTotalsDialogComponent, { data });
  }

  creaBrogliaccio(): void {
    if (!this.almenoUnIncassoIncluso || this.isSaving) {
      return;
    }

    const idIncassi = this.incassiDaRendicontare
      .filter(incasso => incasso.selezionato)
      .map(incasso => incasso.idIncasso);

    this.isSaving = true;

    this.worksheetsService
      .createWorksheet(idIncassi)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: () => this.tornaIndietro(),
        error: err => {
          console.error('Errore creazione brogliaccio', err);
          this.snackBar.open(err?.error?.detail ?? translate<string>('receipts.ui.creazioneDelBrogliaccioNonRiuscita'), translate<string>('receipts.ui.chiudi'), {
            duration: 5000,
          });
        },
      });
  }

  tornaIndietro(): void {
    this.location.back();
  }

  private toRow(receipt: Receipt): ReceiptRow {
    const contanti = receipt.totaleValuta ?? 0;
    const assegni = receipt.totaleAssegni ?? 0;

    return {
      id: String(receipt.numeroIncasso),
      idIncasso: receipt.id,
      zona: receipt.zona,
      cliente: receipt.ragioneSocialeCliente,
      dataIncasso: receipt.dataIncasso ?? '',
      importoContanti: contanti,
      importoAssegni: assegni,
      importoTotale: receipt.totaleIncasso ?? contanti + assegni,
      selezionato: false,
    };
  }

  private calcolaTotaleSelezionato(
    campo: 'importoContanti' | 'importoAssegni' | 'importoTotale',
  ): number {
    return this.incassiDaRendicontare
      .filter(incasso => incasso.selezionato)
      .reduce((totale, incasso) => totale + incasso[campo], 0);
  }
}
